import { useRef, useState, useEffect } from 'react'
import { useAuth } from '../../lib/auth'
import { Button } from '../ui/button'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { 
  Plus,
  Settings,
  User,
  LogOut,
  SidebarOpen,
  SidebarClose
} from 'lucide-react'
import { ThemeMenuItem } from '../ui/theme-toggle'
import { NoteListRef } from './NoteList'
import { Sidebar } from './Sidebar'
import { Editor, EditorRef } from '../editor'
import { useNotes } from '../../hooks/useNotes'
import { useUIStore } from '../../store/uiStore'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'

export function AppLayout() {
  const { user, signOut } = useAuth()
  const { selectedNote, createNote } = useNotes()
  const { sidebarCollapsed, toggleSidebar, sidebarWidth } = useUIStore()
  const editorRef = useRef<EditorRef>(null)
  const noteListRef = useRef<NoteListRef>(null)
  const [noteTitle, setNoteTitle] = useState('')
  const [noteContent, setNoteContent] = useState('')

  // Initialize note title and content when selected note changes
  useEffect(() => {
    setNoteTitle(selectedNote?.title || '')
    setNoteContent(selectedNote?.content || '')
  }, [selectedNote?.id])

  const handleSignOut = async () => {
    await signOut()
  }

  const handleNewNote = async () => {
    const note = await createNote()
    if (note && editorRef.current) {
      // Small delay to ensure the note is selected
      setTimeout(() => {
        editorRef.current?.focus()
      }, 100)
    }
  }

  const handleTitleChange = (title: string) => {
    setNoteTitle(title)
  }

  const handleContentChange = (content: string) => {
    setNoteContent(content)
  }

  const focusEditor = () => {
    editorRef.current?.focus()
  }

  const focusSearch = () => {
    noteListRef.current?.focusSearch()
  }

  // Set up keyboard shortcuts
  useKeyboardShortcuts({
    noteId: selectedNote?.id,
    content: noteContent,
    title: noteTitle,
    onFocusEditor: focusEditor,
    onFocusSearch: focusSearch
  })

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border px-4 py-3 flex items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="p-2"
          >
            {sidebarCollapsed ? <SidebarOpen className="h-4 w-4" /> : <SidebarClose className="h-4 w-4" />}
          </Button>
          <h1 className="text-xl font-bold tracking-tight">Memora Notes</h1>
          {/* <Separator orientation="vertical" className="h-6" /> */}
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline-block">
                  {user?.email?.split('@')[0]}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">Account</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <ThemeMenuItem />
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar with Tags and Notes - conditionally render based on collapse state */}
        {!sidebarCollapsed && (
          <Sidebar 
            style={{ width: sidebarWidth }} 
            noteListRef={noteListRef}
          />
        )}

        {/* Editor */}
        <main className="flex-1 bg-background flex flex-col">
          {selectedNote ? (
            <Editor
              ref={editorRef}
              noteId={selectedNote.id}
              content={noteContent}
              title={noteTitle}
              onContentChange={handleContentChange}
              onTitleChange={handleTitleChange}
              autoFocus
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="max-w-md text-center">
                <h2 className="text-2xl font-bold mb-4">Welcome to Memora Notes</h2>
                <p className="text-muted-foreground mb-6">
                  {sidebarCollapsed 
                    ? "Open the sidebar to see your notes or create a new one to get started."
                    : "Select a note from the sidebar or create a new one to get started."
                  }
                </p>
                <Button onClick={handleNewNote} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create your first note
                </Button>
                <div className="mt-8 space-y-4 text-sm text-muted-foreground">
                  <div>
                    <strong>Keyboard shortcuts:</strong>
                    <ul className="mt-2 space-y-1">
                      <li>• <kbd className="px-2 py-1 bg-muted rounded text-xs">Cmd/Ctrl + N</kbd> - New note</li>
                      <li>• <kbd className="px-2 py-1 bg-muted rounded text-xs">Cmd/Ctrl + S</kbd> - Save note</li>
                      <li>• <kbd className="px-2 py-1 bg-muted rounded text-xs">Cmd/Ctrl + F</kbd> - Search</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
